'use client';

import { c as _c } from "react/compiler-runtime";
import { platform } from '@todesktop/client-core';
import { MoreHorizontal, Sparkles } from 'lucide-react';
import Link from 'next/link';
import { useCallback, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { toast } from 'sonner';
import { usePrV2PullRequestActions } from '../hooks/use-pr-v2-pull-request-actions';
import { useZenModeToggle } from '../hooks/use-zen-mode-toggle';
import { PrV2SettingsRadioSubmenu } from './pr-v2-settings-radio-submenu';
import { usePrV2RepositoryIdResolution } from './shared/use-pr-v2-repository-id';
import { confirmCommandAction } from '@/commands/confirm-command-action';
import { prV2Command } from '@/commands/modules/pr-v2-command-group';
import { useRegisterCommands } from '@/commands/use-commands';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useQueryAIReviewSettings } from '@/data/ai-review/settings/use-query-ai-review-settings';
import { useTriggerAiReview } from '@/data/ai-review/use-trigger-ai-review';
import { useTriggerUltrareview } from '@/data/ai-review/use-trigger-ultrareview';
import { useCustomRules } from '@/data/custom-rules/use-custom-rules';
import { usePrV2Refresh } from '@/data/v2/pr/use-pr-v2-refresh';
import { useIsMobile } from '@/hooks/use-mobile';
import { buildDesktopAppLink } from '@/lib/desktop-handoff/open-in-app';
import { useInstallationStore } from '@/lib/stores/installation-store';
import { PR_V2_DIFF_STYLE_OPTIONS, PR_V2_HIGHLIGHT_THEME_OPTIONS, PR_V2_OVERFLOW_OPTIONS, usePrV2ViewPreferencesStore } from '@/lib/stores/pr-v2-view-preferences-store';
import { altChar, metaChar } from '@/lib/utils';
import { buildPrPath } from '@/utils/app-paths';
type PrV2PullRequestRef = {
  owner: string;
  repo: string;
  number: string;
};
type PrV2ActionsDropdownProps = PrV2PullRequestRef & {
  /**
   * Shows the PR page's shortcut hints and its Zen mode item. Turn off where
   * `PrV2ActionsHotkeys` isn't mounted and there is no page chrome to hide
   * (the /code session panel).
   */
  showShortcuts?: boolean;
  /**
   * Path that "Copy link" and "Copy title as link" copy on the current origin.
   * Defaults to the current page URL, which on the PR page keeps its view.
   */
  linkPath?: string;
};
function AIReviewDialog(t0) {
  const $ = _c(12);
  const {
    open,
    onOpenChange,
    installationId,
    repositoryId
  } = t0;
  const {
    setSelectedInstallationId
  } = useInstallationStore();
  let t1;
  if ($[0] !== installationId || $[1] !== onOpenChange || $[2] !== setSelectedInstallationId) {
    t1 = () => {
      setSelectedInstallationId(installationId);
      onOpenChange(false);
    };
    $[0] = installationId;
    $[1] = onOpenChange;
    $[2] = setSelectedInstallationId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleEnableAIReview = t1;
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <DialogHeader><DialogTitle>AI review is disabled</DialogTitle><DialogDescription>Enable AI review to get automated feedback on your pull requests.</DialogDescription></DialogHeader>;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = `/ai-review${repositoryId ? `?repoId=${repositoryId}` : ""}`;
  let t4;
  if ($[5] !== handleEnableAIReview || $[6] !== t3) {
    t4 = <DialogContent>{t2}<DialogFooter><Button asChild={true}><Link href={t3} target="_blank" onClick={handleEnableAIReview}>Enable AI review</Link></Button></DialogFooter></DialogContent>;
    $[5] = handleEnableAIReview;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== onOpenChange || $[9] !== open || $[10] !== t4) {
    t5 = <Dialog open={open} onOpenChange={onOpenChange}>{t4}</Dialog>;
    $[8] = onOpenChange;
    $[9] = open;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}

/**
 * The PR page's keyboard shortcuts for the actions menu. Rendered beside
 * `PrV2ActionsDropdown` on the PR page only.
 */
export function PrV2ActionsHotkeys(t0) {
  const $ = _c(60);
  const {
    owner,
    repo,
    number
  } = t0;
  const {
    pullRequest,
    previewUrl,
    isOpen,
    isUpdatingState,
    openOnGitHub,
    openPreviewDeployment,
    toggleDraft,
    setPullRequestState,
    copyUrl,
    copyBranchName,
    copyBranchHash,
    copyTitleAsLink
  } = usePrV2PullRequestActions(owner, repo, number);
  let t1;
  if ($[0] !== openOnGitHub) {
    t1 = event => {
      event.preventDefault();
      openOnGitHub();
    };
    $[0] = openOnGitHub;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = Boolean(pullRequest?.url);
  let t3;
  if ($[2] !== t2) {
    t3 = {
      enableOnFormTags: false,
      enabled: t2
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = pullRequest?.url;
  let t5;
  if ($[4] !== openOnGitHub || $[5] !== t4) {
    t5 = [openOnGitHub, t4];
    $[4] = openOnGitHub;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  useHotkeys("shift+g", t1, t3, t5);
  let t6;
  if ($[7] !== toggleDraft) {
    t6 = event_0 => {
      event_0.preventDefault();
      toggleDraft();
    };
    $[7] = toggleDraft;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = Boolean(pullRequest) && !pullRequest?.draft && !isUpdatingState;
  let t8;
  if ($[9] !== t7) {
    t8 = {
      enableOnFormTags: false,
      enabled: t7
    };
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== isUpdatingState || $[12] !== pullRequest || $[13] !== toggleDraft) {
    t9 = [pullRequest, toggleDraft, isUpdatingState];
    $[11] = isUpdatingState;
    $[12] = pullRequest;
    $[13] = toggleDraft;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  useHotkeys("shift+d", t6, t8, t9);
  let t10;
  if ($[15] !== toggleDraft) {
    t10 = event_1 => {
      event_1.preventDefault();
      toggleDraft();
    };
    $[15] = toggleDraft;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const t11 = Boolean(pullRequest?.draft) && !isUpdatingState;
  let t12;
  if ($[17] !== t11) {
    t12 = {
      enableOnFormTags: false,
      enabled: t11
    };
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== isUpdatingState || $[20] !== pullRequest || $[21] !== toggleDraft) {
    t13 = [pullRequest, toggleDraft, isUpdatingState];
    $[19] = isUpdatingState;
    $[20] = pullRequest;
    $[21] = toggleDraft;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  useHotkeys("shift+p", t10, t12, t13);
  let t14;
  if ($[23] !== isOpen || $[24] !== setPullRequestState) {
    t14 = event_2 => {
      event_2.preventDefault();
      setPullRequestState(isOpen ? "closed" : "open");
    };
    $[23] = isOpen;
    $[24] = setPullRequestState;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  const t15 = Boolean(pullRequest) && !isUpdatingState;
  let t16;
  if ($[26] !== t15) {
    t16 = {
      enableOnFormTags: false,
      enabled: t15
    };
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== isOpen || $[29] !== isUpdatingState || $[30] !== pullRequest || $[31] !== setPullRequestState) {
    t17 = [isOpen, pullRequest, setPullRequestState, isUpdatingState];
    $[28] = isOpen;
    $[29] = isUpdatingState;
    $[30] = pullRequest;
    $[31] = setPullRequestState;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  useHotkeys("shift+x", t14, t16, t17);
  let t18;
  if ($[33] !== openPreviewDeployment) {
    t18 = event_3 => {
      event_3.preventDefault();
      openPreviewDeployment();
    };
    $[33] = openPreviewDeployment;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  const t19 = Boolean(previewUrl);
  let t20;
  if ($[35] !== t19) {
    t20 = {
      enableOnFormTags: false,
      enabled: t19
    };
    $[35] = t19;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== openPreviewDeployment || $[38] !== previewUrl) {
    t21 = [openPreviewDeployment, previewUrl];
    $[37] = openPreviewDeployment;
    $[38] = previewUrl;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  useHotkeys("alt+p", t18, t20, t21);
  let t22;
  if ($[40] !== copyUrl) {
    t22 = event_4 => {
      event_4.preventDefault();
      copyUrl();
    };
    $[40] = copyUrl;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {
      enableOnFormTags: false
    };
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  let t24;
  if ($[43] !== copyUrl) {
    t24 = [copyUrl];
    $[43] = copyUrl;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  useHotkeys("c>l", t22, t23, t24);
  let t25;
  if ($[45] !== copyBranchName) {
    t25 = event_5 => {
      event_5.preventDefault();
      copyBranchName();
    };
    $[45] = copyBranchName;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = {
      enableOnFormTags: false
    };
    $[47] = t26;
  } else {
    t26 = $[47];
  }
  let t27;
  if ($[48] !== copyBranchName) {
    t27 = [copyBranchName];
    $[48] = copyBranchName;
    $[49] = t27;
  } else {
    t27 = $[49];
  }
  useHotkeys("c>b", t25, t26, t27);
  let t28;
  if ($[50] !== copyBranchHash) {
    t28 = event_6 => {
      event_6.preventDefault();
      copyBranchHash();
    };
    $[50] = copyBranchHash;
    $[51] = t28;
  } else {
    t28 = $[51];
  }
  let t29;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = {
      enableOnFormTags: false
    };
    $[52] = t29;
  } else {
    t29 = $[52];
  }
  let t30;
  if ($[53] !== copyBranchHash) {
    t30 = [copyBranchHash];
    $[53] = copyBranchHash;
    $[54] = t30;
  } else {
    t30 = $[54];
  }
  useHotkeys("c>h", t28, t29, t30);
  let t31;
  if ($[55] !== copyTitleAsLink) {
    t31 = event_7 => {
      event_7.preventDefault();
      copyTitleAsLink();
    };
    $[55] = copyTitleAsLink;
    $[56] = t31;
  } else {
    t31 = $[56];
  }
  let t32;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = {
      enableOnFormTags: false
    };
    $[57] = t32;
  } else {
    t32 = $[57];
  }
  let t33;
  if ($[58] !== copyTitleAsLink) {
    t33 = [copyTitleAsLink];
    $[58] = copyTitleAsLink;
    $[59] = t33;
  } else {
    t33 = $[59];
  }
  useHotkeys("c>t", t31, t32, t33);
  return null;
}
function PrV2ZenModeMenuItem() {
  const $ = _c(6);
  const {
    isZenMode,
    toggle: toggleZenMode
  } = useZenModeToggle();
  let t0;
  if ($[0] !== toggleZenMode) {
    t0 = () => toggleZenMode();
    $[0] = toggleZenMode;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = isZenMode ? "Disable Zen mode" : "Enable Zen mode";
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <DropdownMenuShortcut>{`${metaChar()}\\`}</DropdownMenuShortcut>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t0 || $[4] !== t1) {
    t3 = <DropdownMenuItem onSelect={t0}>{t1}{t2}</DropdownMenuItem>;
    $[3] = t0;
    $[4] = t1;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
export function PrV2ActionsDropdown(t0) {
  const $ = _c(189);
  const {
    owner,
    repo,
    number,
    showShortcuts: t1,
    linkPath
  } = t0;
  const showShortcuts = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== linkPath) {
    t2 = {
      linkPath
    };
    $[0] = linkPath;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    pullRequest,
    previewUrl,
    isOpen,
    isUpdatingState,
    openOnGitHub,
    openPreviewDeployment,
    toggleDraft,
    setPullRequestState,
    copyUrl,
    copyPrNumber,
    copyBranchName,
    copyBranchHash,
    copyTitleAsLink
  } = usePrV2PullRequestActions(owner, repo, number, t2);
  const refreshMutation = usePrV2Refresh(owner, repo, number);
  const isMobile = useIsMobile();
  const {
    mutate: triggerAiReview,
    isPending: isReviewLoading
  } = useTriggerAiReview();
  const {
    mutate: triggerUltrareview,
    isPending: isUltrareviewLoading
  } = useTriggerUltrareview();
  const diffStyle = usePrV2ViewPreferencesStore(_temp);
  const overflow = usePrV2ViewPreferencesStore(_temp2);
  const highlightTheme = usePrV2ViewPreferencesStore(_temp3);
  const expandUnchanged = usePrV2ViewPreferencesStore(_temp4);
  const hideTestFiles = usePrV2ViewPreferencesStore(_temp5);
  const applyPreferences = usePrV2ViewPreferencesStore(_temp6);
  const [open, setOpen] = useState(false);
  const [showAiReviewDialog, setShowAiReviewDialog] = useState(false);
  const installationId = pullRequest?.installationId ?? null;
  const summaryRepositoryId = pullRequest?.repositoryId ?? null;
  const t3 = Boolean(installationId);
  let t4;
  if ($[2] !== installationId || $[3] !== owner || $[4] !== repo || $[5] !== t3) {
    t4 = {
      owner,
      repo,
      installationId,
      enabled: t3
    };
    $[2] = installationId;
    $[3] = owner;
    $[4] = repo;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    repositoryId: fallbackRepositoryId,
    repositoryIsPrivate
  } = usePrV2RepositoryIdResolution(t4);
  const repositoryId = summaryRepositoryId ?? fallbackRepositoryId;
  const hasRepositoryId = repositoryId != null;
  let t5;
  if ($[7] !== hasRepositoryId || $[8] !== installationId || $[9] !== repositoryId || $[10] !== repositoryIsPrivate) {
    t5 = {
      installationId,
      repositoryId,
      repositoryIsPrivate,
      enabled: hasRepositoryId
    };
    $[7] = hasRepositoryId;
    $[8] = installationId;
    $[9] = repositoryId;
    $[10] = repositoryIsPrivate;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const {
    settings
  } = useQueryAIReviewSettings(t5);
  let t6;
  if ($[12] !== hasRepositoryId) {
    t6 = {
      enabled: hasRepositoryId
    };
    $[12] = hasRepositoryId;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const {
    rules
  } = useCustomRules(installationId, repositoryId, t6);
  let t7;
  if ($[14] !== rules) {
    t7 = Array.isArray(rules) && rules.some(_temp7);
    $[14] = rules;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const hasEnabledCustomRules = t7;
  const isGeneralReviewEnabled = settings?.generalReviewEnabled ?? false;
  const isAiReviewConfigured = isGeneralReviewEnabled || hasEnabledCustomRules;
  let t8;
  if ($[16] !== applyPreferences) {
    t8 = (key, value) => {
      applyPreferences({
        [key]: value
      });
    };
    $[16] = applyPreferences;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const updatePreference = t8;
  let t9;
  if ($[18] !== number || $[19] !== owner || $[20] !== repo) {
    t9 = () => {
      window.location.assign(buildDesktopAppLink(buildPrPath({
        owner,
        repo,
        number
      })));
    };
    $[18] = number;
    $[19] = owner;
    $[20] = repo;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const openInDesktop = t9;
  let t10;
  if ($[22] !== refreshMutation) {
    t10 = async () => {
      ;
      try {
        await refreshMutation.mutateAsync();
        toast.success("Refresh queued");
      } catch (t11) {
        const error = t11;
        toast.error(error instanceof Error ? error.message : "Failed to refresh PR cache");
      }
    };
    $[22] = refreshMutation;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const refreshCache = t10;
  let t11;
  if ($[24] !== installationId || $[25] !== isAiReviewConfigured || $[26] !== number || $[27] !== owner || $[28] !== repo || $[29] !== triggerAiReview) {
    t11 = () => {
      if (!installationId) {
        toast.error("Unable to trigger AI review: installation ID not found");
        return;
      }
      if (!isAiReviewConfigured) {
        setShowAiReviewDialog(true);
        return;
      }
      triggerAiReview({
        owner,
        repo,
        number,
        installationId
      });
    };
    $[24] = installationId;
    $[25] = isAiReviewConfigured;
    $[26] = number;
    $[27] = owner;
    $[28] = repo;
    $[29] = triggerAiReview;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const handleTriggerAiReview = t11;
  let t12;
  let t13;
  if ($[31] !== handleTriggerAiReview || $[32] !== installationId || $[33] !== isAiReviewConfigured || $[34] !== isReviewLoading) {
    t12 = () => {
      if (!installationId || isReviewLoading) {
        return [];
      }
      return [prV2Command("pr-v2-run-ai-review", {
        label: "Run AI review...",
        icon: <Sparkles />,
        action: () => isAiReviewConfigured ? confirmCommandAction("Run AI review on this pull request?", handleTriggerAiReview) : handleTriggerAiReview(),
        keywords: ["ai", "review", "automation"]
      })];
    };
    t13 = [handleTriggerAiReview, installationId, isAiReviewConfigured, isReviewLoading];
    $[31] = handleTriggerAiReview;
    $[32] = installationId;
    $[33] = isAiReviewConfigured;
    $[34] = isReviewLoading;
    $[35] = t12;
    $[36] = t13;
  } else {
    t12 = $[35];
    t13 = $[36];
  }
  useRegisterCommands(t12, t13);
  let t14;
  if ($[37] !== installationId || $[38] !== number || $[39] !== owner || $[40] !== repo || $[41] !== triggerUltrareview) {
    t14 = () => {
      if (!installationId) {
        toast.error("Unable to trigger ultrareview: installation ID not found");
        return;
      }
      triggerUltrareview({
        owner,
        repo,
        number,
        installationId
      });
    };
    $[37] = installationId;
    $[38] = number;
    $[39] = owner;
    $[40] = repo;
    $[41] = triggerUltrareview;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  const handleTriggerUltrareview = t14;
  let t15;
  if ($[43] !== showShortcuts) {
    t15 = keys => showShortcuts ? <DropdownMenuShortcut>{keys}</DropdownMenuShortcut> : null;
    $[43] = showShortcuts;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  const shortcutHint = t15;
  let t16;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <DropdownMenuTrigger asChild={true}><Button size="icon" variant="ghost" className="size-8 text-zinc-500 [&_svg]:size-3.5" aria-label="Pull request actions"><MoreHorizontal className="size-3.5" /></Button></DropdownMenuTrigger>;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  const t17 = !pullRequest?.url;
  let t18;
  if ($[46] !== shortcutHint) {
    t18 = shortcutHint("\u21E7G");
    $[46] = shortcutHint;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== openOnGitHub || $[49] !== t17 || $[50] !== t18) {
    t19 = <DropdownMenuItem onSelect={openOnGitHub} disabled={t17}>Open in GitHub{t18}</DropdownMenuItem>;
    $[48] = openOnGitHub;
    $[49] = t17;
    $[50] = t18;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== openInDesktop) {
    t20 = !platform.todesktop.isDesktopApp() ? <DropdownMenuItem onSelect={openInDesktop}>Open in Desktop</DropdownMenuItem> : null;
    $[52] = openInDesktop;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  let t21;
  if ($[54] !== openPreviewDeployment || $[55] !== previewUrl || $[56] !== shortcutHint) {
    t21 = previewUrl ? <DropdownMenuItem onSelect={openPreviewDeployment}>Open preview deployment{shortcutHint(`${altChar()}P`)}</DropdownMenuItem> : null;
    $[54] = openPreviewDeployment;
    $[55] = previewUrl;
    $[56] = shortcutHint;
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  let t22;
  if ($[58] !== refreshCache) {
    t22 = () => void refreshCache();
    $[58] = refreshCache;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  const t23 = refreshMutation.isPending ? "Refreshing\u2026" : "Refresh cache";
  let t24;
  if ($[60] !== refreshMutation.isPending || $[61] !== t22 || $[62] !== t23) {
    t24 = <DropdownMenuItem onSelect={t22} disabled={refreshMutation.isPending}>{t23}</DropdownMenuItem>;
    $[60] = refreshMutation.isPending;
    $[61] = t22;
    $[62] = t23;
    $[63] = t24;
  } else {
    t24 = $[63];
  }
  let t25;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <DropdownMenuSeparator />;
    $[64] = t25;
  } else {
    t25 = $[64];
  }
  let t26;
  if ($[65] !== toggleDraft) {
    t26 = () => void toggleDraft();
    $[65] = toggleDraft;
    $[66] = t26;
  } else {
    t26 = $[66];
  }
  const t27 = isUpdatingState || !pullRequest;
  const t28 = pullRequest?.draft ? "Undraft PR" : "Convert to draft";
  const t29 = pullRequest?.draft ? "\u21E7P" : "\u21E7D";
  let t30;
  if ($[67] !== shortcutHint || $[68] !== t29) {
    t30 = shortcutHint(t29);
    $[67] = shortcutHint;
    $[68] = t29;
    $[69] = t30;
  } else {
    t30 = $[69];
  }
  let t31;
  if ($[70] !== t26 || $[71] !== t27 || $[72] !== t28 || $[73] !== t30) {
    t31 = <DropdownMenuItem onSelect={t26} disabled={t27}>{t28}{t30}</DropdownMenuItem>;
    $[70] = t26;
    $[71] = t27;
    $[72] = t28;
    $[73] = t30;
    $[74] = t31;
  } else {
    t31 = $[74];
  }
  let t32;
  if ($[75] !== isOpen || $[76] !== setPullRequestState) {
    t32 = () => void setPullRequestState(isOpen ? "closed" : "open");
    $[75] = isOpen;
    $[76] = setPullRequestState;
    $[77] = t32;
  } else {
    t32 = $[77];
  }
  const t33 = isUpdatingState || !pullRequest;
  const t34 = isOpen ? "Close pull request" : "Reopen pull request";
  let t35;
  if ($[78] !== shortcutHint) {
    t35 = shortcutHint("\u21E7X");
    $[78] = shortcutHint;
    $[79] = t35;
  } else {
    t35 = $[79];
  }
  let t36;
  if ($[80] !== t32 || $[81] !== t33 || $[82] !== t34 || $[83] !== t35) {
    t36 = <DropdownMenuItem onSelect={t32} disabled={t33}>{t34}{t35}</DropdownMenuItem>;
    $[80] = t32;
    $[81] = t33;
    $[82] = t34;
    $[83] = t35;
    $[84] = t36;
  } else {
    t36 = $[84];
  }
  let t37;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <DropdownMenuSeparator />;
    $[85] = t37;
  } else {
    t37 = $[85];
  }
  const t38 = isReviewLoading || !installationId;
  const t39 = isReviewLoading ? "Running AI review..." : "Run AI review on PR";
  let t40;
  if ($[86] !== handleTriggerAiReview || $[87] !== t38 || $[88] !== t39) {
    t40 = <DropdownMenuItem onSelect={handleTriggerAiReview} disabled={t38} className="flex items-center">{t39}</DropdownMenuItem>;
    $[86] = handleTriggerAiReview;
    $[87] = t38;
    $[88] = t39;
    $[89] = t40;
  } else {
    t40 = $[89];
  }
  const t41 = isUltrareviewLoading || !installationId;
  const t42 = isUltrareviewLoading ? "Running ultrareview..." : "Run ultrareview on PR";
  let t43;
  if ($[90] !== handleTriggerUltrareview || $[91] !== t41 || $[92] !== t42) {
    t43 = <DropdownMenuItem onSelect={handleTriggerUltrareview} disabled={t41} className="flex items-center">{t42}</DropdownMenuItem>;
    $[90] = handleTriggerUltrareview;
    $[91] = t41;
    $[92] = t42;
    $[93] = t43;
  } else {
    t43 = $[93];
  }
  let t44;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <DropdownMenuSeparator />;
    $[94] = t44;
  } else {
    t44 = $[94];
  }
  let t45;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <DropdownMenuSubTrigger>Diff settings</DropdownMenuSubTrigger>;
    $[95] = t45;
  } else {
    t45 = $[95];
  }
  let t46;
  if ($[96] !== diffStyle || $[97] !== isMobile || $[98] !== updatePreference) {
    t46 = !isMobile && <PrV2SettingsRadioSubmenu label="Layout" value={diffStyle} options={PR_V2_DIFF_STYLE_OPTIONS} onValueChange={value_0 => updatePreference("diffStyle", value_0)} />;
    $[96] = diffStyle;
    $[97] = isMobile;
    $[98] = updatePreference;
    $[99] = t46;
  } else {
    t46 = $[99];
  }
  let t47;
  if ($[100] !== updatePreference) {
    t47 = value_1 => updatePreference("overflow", value_1);
    $[100] = updatePreference;
    $[101] = t47;
  } else {
    t47 = $[101];
  }
  let t48;
  if ($[102] !== overflow || $[103] !== t47) {
    t48 = <PrV2SettingsRadioSubmenu label="Overflow" value={overflow} options={PR_V2_OVERFLOW_OPTIONS} onValueChange={t47} />;
    $[102] = overflow;
    $[103] = t47;
    $[104] = t48;
  } else {
    t48 = $[104];
  }
  let t49;
  if ($[105] !== updatePreference) {
    t49 = value_2 => updatePreference("highlightTheme", value_2);
    $[105] = updatePreference;
    $[106] = t49;
  } else {
    t49 = $[106];
  }
  let t50;
  if ($[107] !== highlightTheme || $[108] !== t49) {
    t50 = <PrV2SettingsRadioSubmenu label="Highlight theme" value={highlightTheme} options={PR_V2_HIGHLIGHT_THEME_OPTIONS} onValueChange={t49} />;
    $[107] = highlightTheme;
    $[108] = t49;
    $[109] = t50;
  } else {
    t50 = $[109];
  }
  let t51;
  if ($[110] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = <DropdownMenuSeparator />;
    $[110] = t51;
  } else {
    t51 = $[110];
  }
  let t52;
  if ($[111] !== updatePreference) {
    t52 = checked => updatePreference("hideTestFiles", Boolean(checked));
    $[111] = updatePreference;
    $[112] = t52;
  } else {
    t52 = $[112];
  }
  let t53;
  if ($[113] !== hideTestFiles || $[114] !== t52) {
    t53 = <DropdownMenuCheckboxItem checked={hideTestFiles} onCheckedChange={t52} onSelect={_temp8}>Hide test files</DropdownMenuCheckboxItem>;
    $[113] = hideTestFiles;
    $[114] = t52;
    $[115] = t53;
  } else {
    t53 = $[115];
  }
  let t54;
  if ($[116] !== updatePreference) {
    t54 = checked_0 => updatePreference("expandUnchanged", Boolean(checked_0));
    $[116] = updatePreference;
    $[117] = t54;
  } else {
    t54 = $[117];
  }
  let t55;
  if ($[118] !== expandUnchanged || $[119] !== t54) {
    t55 = <DropdownMenuCheckboxItem checked={expandUnchanged} onCheckedChange={t54} onSelect={_temp9}>Expand unchanged</DropdownMenuCheckboxItem>;
    $[118] = expandUnchanged;
    $[119] = t54;
    $[120] = t55;
  } else {
    t55 = $[120];
  }
  let t56;
  if ($[121] !== t46 || $[122] !== t48 || $[123] !== t50 || $[124] !== t53 || $[125] !== t55) {
    t56 = <DropdownMenuSub>{t45}<DropdownMenuSubContent className="max-h-[65vh] w-52 overflow-y-auto">{t46}{t48}{t50}{t51}{t53}{t55}</DropdownMenuSubContent></DropdownMenuSub>;
    $[121] = t46;
    $[122] = t48;
    $[123] = t50;
    $[124] = t53;
    $[125] = t55;
    $[126] = t56;
  } else {
    t56 = $[126];
  }
  let t57;
  if ($[127] !== showShortcuts) {
    t57 = showShortcuts ? <PrV2ZenModeMenuItem /> : null;
    $[127] = showShortcuts;
    $[128] = t57;
  } else {
    t57 = $[128];
  }
  let t58;
  if ($[129] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = <DropdownMenuSeparator />;
    $[129] = t58;
  } else {
    t58 = $[129];
  }
  let t59;
  if ($[130] === Symbol.for("react.memo_cache_sentinel")) {
    t59 = <DropdownMenuSubTrigger>Copy</DropdownMenuSubTrigger>;
    $[130] = t59;
  } else {
    t59 = $[130];
  }
  let t60;
  if ($[131] !== copyUrl) {
    t60 = () => void copyUrl();
    $[131] = copyUrl;
    $[132] = t60;
  } else {
    t60 = $[132];
  }
  let t61;
  if ($[133] !== shortcutHint) {
    t61 = shortcutHint("C L");
    $[133] = shortcutHint;
    $[134] = t61;
  } else {
    t61 = $[134];
  }
  let t62;
  if ($[135] !== t60 || $[136] !== t61) {
    t62 = <DropdownMenuItem onSelect={t60}>Copy link{t61}</DropdownMenuItem>;
    $[135] = t60;
    $[136] = t61;
    $[137] = t62;
  } else {
    t62 = $[137];
  }
  let t63;
  if ($[138] !== copyPrNumber) {
    t63 = <DropdownMenuItem onSelect={() => void copyPrNumber()}>Copy PR number</DropdownMenuItem>;
    $[138] = copyPrNumber;
    $[139] = t63;
  } else {
    t63 = $[139];
  }
  let t64;
  if ($[140] !== copyBranchName) {
    t64 = () => void copyBranchName();
    $[140] = copyBranchName;
    $[141] = t64;
  } else {
    t64 = $[141];
  }
  let t65;
  if ($[142] !== shortcutHint) {
    t65 = shortcutHint("C B");
    $[142] = shortcutHint;
    $[143] = t65;
  } else {
    t65 = $[143];
  }
  let t66;
  if ($[144] !== t64 || $[145] !== t65) {
    t66 = <DropdownMenuItem onSelect={t64}>Copy branch name{t65}</DropdownMenuItem>;
    $[144] = t64;
    $[145] = t65;
    $[146] = t66;
  } else {
    t66 = $[146];
  }
  let t67;
  if ($[147] !== copyBranchHash) {
    t67 = () => void copyBranchHash();
    $[147] = copyBranchHash;
    $[148] = t67;
  } else {
    t67 = $[148];
  }
  let t68;
  if ($[149] !== shortcutHint) {
    t68 = shortcutHint("C H");
    $[149] = shortcutHint;
    $[150] = t68;
  } else {
    t68 = $[150];
  }
  let t69;
  if ($[151] !== t67 || $[152] !== t68) {
    t69 = <DropdownMenuItem onSelect={t67}>Copy branch hash{t68}</DropdownMenuItem>;
    $[151] = t67;
    $[152] = t68;
    $[153] = t69;
  } else {
    t69 = $[153];
  }
  let t70;
  if ($[154] !== copyTitleAsLink) {
    t70 = () => void copyTitleAsLink();
    $[154] = copyTitleAsLink;
    $[155] = t70;
  } else {
    t70 = $[155];
  }
  let t71;
  if ($[156] !== shortcutHint) {
    t71 = shortcutHint("C T");
    $[156] = shortcutHint;
    $[157] = t71;
  } else {
    t71 = $[157];
  }
  let t72;
  if ($[158] !== t70 || $[159] !== t71) {
    t72 = <DropdownMenuItem onSelect={t70}>Copy title as link{t71}</DropdownMenuItem>;
    $[158] = t70;
    $[159] = t71;
    $[160] = t72;
  } else {
    t72 = $[160];
  }
  let t73;
  if ($[161] !== t62 || $[162] !== t63 || $[163] !== t66 || $[164] !== t69 || $[165] !== t72) {
    t73 = <DropdownMenuSub>{t59}<DropdownMenuSubContent>{t62}{t63}{t66}{t69}{t72}</DropdownMenuSubContent></DropdownMenuSub>;
    $[161] = t62;
    $[162] = t63;
    $[163] = t66;
    $[164] = t69;
    $[165] = t72;
    $[166] = t73;
  } else {
    t73 = $[166];
  }
  let t74;
  if ($[167] !== t19 || $[168] !== t20 || $[169] !== t21 || $[170] !== t24 || $[171] !== t31 || $[172] !== t36 || $[173] !== t40 || $[174] !== t43 || $[175] !== t56 || $[176] !== t57 || $[177] !== t73) {
    t74 = <DropdownMenuContent align="start" collisionPadding={8}>{t19}{t20}{t21}{t24}{t25}{t31}{t36}{t37}{t40}{t43}{t44}{t56}{t57}{t58}{t73}</DropdownMenuContent>;
    $[167] = t19;
    $[168] = t20;
    $[169] = t21;
    $[170] = t24;
    $[171] = t31;
    $[172] = t36;
    $[173] = t40;
    $[174] = t43;
    $[175] = t56;
    $[176] = t57;
    $[177] = t73;
    $[178] = t74;
  } else {
    t74 = $[178];
  }
  let t75;
  if ($[179] !== open || $[180] !== t74) {
    t75 = <DropdownMenu open={open} onOpenChange={setOpen}>{t16}{t74}</DropdownMenu>;
    $[179] = open;
    $[180] = t74;
    $[181] = t75;
  } else {
    t75 = $[181];
  }
  let t76;
  if ($[182] !== installationId || $[183] !== repositoryId || $[184] !== showAiReviewDialog) {
    t76 = installationId ? <AIReviewDialog open={showAiReviewDialog} onOpenChange={setShowAiReviewDialog} installationId={installationId} repositoryId={repositoryId} /> : null;
    $[182] = installationId;
    $[183] = repositoryId;
    $[184] = showAiReviewDialog;
    $[185] = t76;
  } else {
    t76 = $[185];
  }
  let t77;
  if ($[186] !== t75 || $[187] !== t76) {
    t77 = <>{t75}{t76}</>;
    $[186] = t75;
    $[187] = t76;
    $[188] = t77;
  } else {
    t77 = $[188];
  }
  return t77;
}
function _temp9(event_0) {
  return event_0.preventDefault();
}
function _temp8(event) {
  return event.preventDefault();
}
function _temp7(rule) {
  return rule.enabled;
}
function _temp6(state_4) {
  return state_4.applyPreferences;
}
function _temp5(state_3) {
  return state_3.hideTestFiles;
}
function _temp4(state_2) {
  return state_2.expandUnchanged;
}
function _temp3(state_1) {
  return state_1.highlightTheme;
}
function _temp2(state_0) {
  return state_0.overflow;
}
function _temp(state) {
  return state.diffStyle;
}